import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Search,
  User,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Eye,
  CheckCircle,
  Clock,
  Star,
  Download,
  X,
  ArrowRight,
  MessageSquare,
  Briefcase,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { Panel, Badge, StatCard, LoadingSpinner, Button } from './components';
import apiService from './services/api';

const STATUS_OPTIONS = [
  { value: 'applied', label: 'Applied' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interview_completed', label: 'Interview Completed' },
  { value: 'offered', label: 'Offered' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'rejected_hard', label: 'Rejected (Auto)' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const SOURCE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'other', label: 'Other' },
];

const VALID_TRANSITIONS = {
  applied: ['screening', 'rejected'],
  screening: ['interview_scheduled', 'rejected'],
  interview_scheduled: ['interview_completed', 'rejected', 'withdrawn'],
  interview_completed: ['offered', 'rejected'],
  offered: ['hired', 'rejected', 'withdrawn'],
  rejected: ['applied'],
  rejected_hard: ['applied'],
  withdrawn: ['applied'],
};

const getStatusBadgeVariant = (s) => {
  switch (s) {
    case 'applied': return 'info';
    case 'screening': return 'warning';
    case 'interview_scheduled': return 'info';
    case 'interview_completed': return 'neutral';
    case 'offered': return 'warning';
    case 'hired': return 'success';
    case 'rejected': return 'error';
    case 'rejected_hard': return 'error';
    case 'withdrawn': return 'neutral';
    default: return 'neutral';
  }
};

const getJobBadgeVariant = (s) => {
  switch (s) {
    case 'open': return 'success';
    case 'paused': return 'warning';
    case 'closed': return 'error';
    default: return 'neutral';
  }
};

const getStatusLabel = (s) => {
  const opt = STATUS_OPTIONS.find(o => o.value === s);
  return opt ? opt.label : s;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function HiringPage() {
  const [candidates, setCandidates] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('candidates');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [saving, setSaving] = useState(false);
  const itemsPerPage = 10;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [candidateData, jobData, metricsData, locationData] = await Promise.all([
        apiService.getCandidates(),
        apiService.getJobPostings(),
        apiService.getHiringMetrics(),
        apiService.getLocations(),
      ]);
      setCandidates(candidateData.results || candidateData || []);
      setJobPostings(jobData.results || jobData || []);
      setMetrics(metricsData || {});
      setLocations((locationData.results || locationData || []).map(l => ({ id: l.id, name: l.name })));
    } catch (error) {
      console.error('Failed to fetch hiring data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort candidates
  const filteredAndSortedCandidates = useMemo(() => {
    let filtered = candidates.filter(candidate => {
      const name = `${candidate.first_name || ''} ${candidate.last_name || ''}`.toLowerCase();
      const matchesSearch =
        name.includes(searchTerm.toLowerCase()) ||
        (candidate.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.phone || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;
      const matchesLocation = locationFilter === 'all' || String(candidate.location) === locationFilter;
      const matchesSource = sourceFilter === 'all' || candidate.source === sourceFilter;

      return matchesSearch && matchesStatus && matchesLocation && matchesSource;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'rating') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      if (sortField === 'first_name') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [candidates, searchTerm, statusFilter, locationFilter, sourceFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedCandidates.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle status transition
  const handleTransition = async (candidateId, newStatus, note = '') => {
    try {
      setSaving(true);
      await apiService.transitionCandidate(candidateId, { status: newStatus, note });
      await fetchData();
      if (selectedCandidate && selectedCandidate.id === candidateId) {
        const updated = await apiService.getCandidate(candidateId);
        setSelectedCandidate(updated);
      }
    } catch (error) {
      console.error('Transition failed:', error);
      alert('Failed to update status: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Calculate statistics from metrics
  const totalCandidates = metrics.total_candidates || 0;
  const hiredCandidates = (metrics.by_status || {}).hired || 0;
  const activeApplications = metrics.active_candidates || 0;
  const interviewScheduled = (metrics.by_status || {}).interview_scheduled || 0;

  if (loading) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 flex items-center justify-center">
        <LoadingSpinner message="Loading hiring data..." />
      </div>
    );
  }

  const tabs = [
    { key: 'candidates', label: 'Candidates' },
    { key: 'jobs', label: 'Job Postings' },
    { key: 'integrations', label: 'Integrations' },
  ];

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Page Header */}
        <div className="mb-3 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Hiring</h1>
            <p className="text-xs text-gray-600 mt-0.5">Candidate pipeline & job postings</p>
          </div>
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Candidate
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          <StatCard value={totalCandidates} label="Total Candidates" icon={User} />
          <StatCard value={hiredCandidates} label="Hired" icon={CheckCircle} />
          <StatCard value={activeApplications} label="Active Applications" icon={Clock} />
          <StatCard value={interviewScheduled} label="Interviews Scheduled" icon={Calendar} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border border-gray-200 rounded bg-white mb-3 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                activeTab === tab.key
                  ? 'bg-red-800 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Candidates Tab */}
        {activeTab === 'candidates' && (
          <div>
            {/* Filters */}
            <Panel className="mb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                >
                  <option value="all">All Status</option>
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={locationFilter}
                  onChange={(e) => { setLocationFilter(e.target.value); setCurrentPage(1); }}
                  className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                >
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                  ))}
                </select>
                <select
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
                  className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                >
                  <option value="all">All Sources</option>
                  {SOURCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </Panel>

            {/* Candidates Table */}
            <Panel noPadding className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('first_name')}
                      >
                        <div className="flex items-center gap-1">
                          <span>Candidate</span>
                          {sortField === 'first_name' && (
                            sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('rating')}
                      >
                        <div className="flex items-center gap-1">
                          <span>Rating</span>
                          {sortField === 'rating' && (
                            sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          <span>Applied</span>
                          {sortField === 'created_at' && (
                            sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentData.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-2 py-6 text-center text-gray-500 text-xs">
                          {candidates.length === 0 ? 'No candidates yet. Add one with the + button.' : 'No candidates match your filters.'}
                        </td>
                      </tr>
                    ) : currentData.map((candidate) => (
                      <tr key={candidate.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-xs">
                          <div className="font-medium text-gray-900">{candidate.first_name} {candidate.last_name || ''}</div>
                          {candidate.experience_years && (
                            <div className="text-gray-500">{candidate.experience_years} yr{candidate.experience_years !== 1 ? 's' : ''} exp</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {candidate.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-900">{candidate.email}</span>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-900">{candidate.phone}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 capitalize">{candidate.source}</td>
                        <td className="px-2 py-1.5 text-xs">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-900">{candidate.location_name || candidate.preferred_locations || '-'}{candidate.zip_code ? ` (${candidate.zip_code})` : ''}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          <Badge variant={getStatusBadgeVariant(candidate.status)} small>
                            {getStatusLabel(candidate.status)}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {candidate.rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
                              <span className="text-gray-900">{candidate.rating}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">{formatDate(candidate.created_at)}</td>
                        <td className="px-2 py-1.5 text-xs">
                          <button
                            onClick={async () => {
                              try {
                                const detail = await apiService.getCandidate(candidate.id);
                                setSelectedCandidate(detail);
                                setShowDetailModal(true);
                              } catch (e) { console.error(e); }
                            }}
                            className="text-gray-400 hover:text-red-800 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-2 flex items-center justify-between">
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  size="sm"
                  variant="secondary"
                >
                  Previous
                </Button>
                <span className="text-xs text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedCandidates.length)} of {filteredAndSortedCandidates.length}
                </span>
                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  size="sm"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Job Postings Tab */}
        {activeTab === 'jobs' && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-900">Job Postings</h3>
              <Button size="sm" onClick={() => setShowJobModal(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />Create Posting
              </Button>
            </div>

            <Panel noPadding className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Salary</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Applications</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Posted</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobPostings.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-2 py-6 text-center text-gray-500 text-xs">
                          No job postings yet.
                        </td>
                      </tr>
                    ) : jobPostings.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-xs font-medium text-gray-900">{job.title}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">{job.location_name || '-'}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 capitalize">{(job.type || '').replace('_', ' ')}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {job.salary_min && job.salary_max
                            ? `$${job.salary_min} - $${job.salary_max}`
                            : job.salary_min ? `From $${job.salary_min}` : '-'
                          }
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          <Badge variant={getJobBadgeVariant(job.status)} small>
                            {job.status}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">{job.candidate_count || 0}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">{formatDate(job.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Hiring Source Integrations</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Website Integration */}
              <Panel>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-red-800 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">WB</span>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-900">Website</div>
                      <div className="text-xs text-gray-500">/join-our-team form</div>
                    </div>
                  </div>
                  <Badge variant="success" small>Active</Badge>
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Applications from Website:</span>
                    <span className="font-medium text-gray-900">{(metrics.by_source || {}).website || 0}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Endpoint:</span>
                    <span className="font-medium text-gray-500">/api/v1/amn/hiring/apply/</span>
                  </div>
                </div>
              </Panel>

              {/* Data Management */}
              <Panel>
                <div className="text-xs font-medium text-gray-900 mb-3">Data Management</div>
                <Button size="sm" variant="secondary" className="w-full">
                  <Download className="w-3.5 h-3.5 mr-1" />Export Candidates
                </Button>
              </Panel>
            </div>
          </div>
        )}

        {/* Floating Add Button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="fixed bottom-3 right-3 w-10 h-10 bg-red-800 rounded-lg flex items-center justify-center hover:bg-red-900 transition-colors border border-red-900"
        >
          <Plus className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Add Candidate Modal */}
      {showAddModal && (
        <AddCandidateModal
          locations={locations}
          jobPostings={jobPostings}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchData(); }}
        />
      )}

      {/* Candidate Detail Modal */}
      {showDetailModal && selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          onClose={() => { setShowDetailModal(false); setSelectedCandidate(null); }}
          onTransition={handleTransition}
          onRefresh={async () => {
            const updated = await apiService.getCandidate(selectedCandidate.id);
            setSelectedCandidate(updated);
            fetchData();
          }}
          saving={saving}
        />
      )}

      {/* Add Job Posting Modal */}
      {showJobModal && (
        <AddJobPostingModal
          locations={locations}
          onClose={() => setShowJobModal(false)}
          onSaved={() => { setShowJobModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}


// =============================================================================
// Add Candidate Modal
// =============================================================================

function AddCandidateModal({ locations, jobPostings, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    source: 'other', location_id: '', job_posting_id: '',
    experience_years: '', has_license: false, has_transportation: false,
    cover_letter: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return;
    try {
      setSaving(true);
      const data = { ...form };
      if (!data.location_id) delete data.location_id;
      if (!data.job_posting_id) delete data.job_posting_id;
      if (!data.experience_years) delete data.experience_years;
      else data.experience_years = parseInt(data.experience_years);
      await apiService.createCandidate(data);
      onSaved();
    } catch (error) {
      alert('Failed to create candidate: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
      <div className="bg-white border border-gray-200 shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10 border-b border-red-900">
          <div className="flex items-center">
            <User className="w-4 h-4 mr-2" />
            <h2 className="text-sm font-semibold">Add Candidate</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-3 py-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <input type="text" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                <select value={form.source} onChange={(e) => setForm({...form, source: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  {SOURCE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <select value={form.location_id} onChange={(e) => setForm({...form, location_id: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  <option value="">Select location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Experience (yrs)</label>
                <input type="number" min="0" value={form.experience_years} onChange={(e) => setForm({...form, experience_years: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={form.has_license} onChange={(e) => setForm({...form, has_license: e.target.checked})} />
                  <span>Has License</span>
                </label>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={form.has_transportation} onChange={(e) => setForm({...form, has_transportation: e.target.checked})} />
                  <span>Has Transport</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.cover_letter} onChange={(e) => setForm({...form, cover_letter: e.target.value})}
                rows={3} className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-3 py-2 flex justify-end gap-2 border-t border-gray-200 sticky bottom-0">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" type="submit" disabled={saving || !form.first_name.trim()}>
              {saving ? 'Saving...' : 'Add Candidate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// =============================================================================
// Candidate Detail Modal (Drawer)
// =============================================================================

function CandidateDetailModal({ candidate, onClose, onTransition, onRefresh, saving }) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const nextStatuses = VALID_TRANSITIONS[candidate.status] || [];

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setAddingNote(true);
      await apiService.createCandidateNote({
        candidate: candidate.id,
        note_type: 'note',
        content: newNote.trim(),
      });
      setNewNote('');
      await onRefresh();
    } catch (error) {
      alert('Failed to add note: ' + error.message);
    } finally {
      setAddingNote(false);
    }
  };

  const handleBeginOnboarding = async () => {
    try {
      const result = await apiService.beginOnboarding(candidate.id);
      await onRefresh();
    } catch (error) {
      alert('Onboarding failed: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-lg overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-red-800 text-white px-3 py-2 flex items-center justify-between border-b border-red-900">
          <div>
            <h2 className="text-sm font-semibold">{candidate.first_name} {candidate.last_name || ''}</h2>
            <div className="mt-1">
              <Badge variant={getStatusBadgeVariant(candidate.status)} small>
                {getStatusLabel(candidate.status)}
              </Badge>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-3">
          {/* Contact Info */}
          <Panel>
            <div className="space-y-1.5">
              {candidate.email && (
                <div className="flex items-center gap-2 text-xs">
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-900">{candidate.email}</span>
                </div>
              )}
              {candidate.phone && (
                <div className="flex items-center gap-2 text-xs">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-900">{candidate.phone}</span>
                </div>
              )}
              {(candidate.location?.name || candidate.preferred_locations || candidate.zip_code) && (
                <div className="flex items-center gap-2 text-xs">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-900">{candidate.location?.name || candidate.preferred_locations || ''}{candidate.zip_code ? ` (${candidate.zip_code})` : ''}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs">
                <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-900">Source: {candidate.source}</span>
              </div>
            </div>
          </Panel>

          {/* Details */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
              <div className="text-xs text-gray-500">Experience</div>
              <div className="text-xs font-medium text-gray-900">{candidate.experience_years ? `${candidate.experience_years} yrs` : '-'}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
              <div className="text-xs text-gray-500">License</div>
              <div className="text-xs font-medium text-gray-900">{candidate.has_license ? 'Yes' : 'No'}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
              <div className="text-xs text-gray-500">Transport</div>
              <div className="text-xs font-medium text-gray-900">{candidate.has_transportation ? 'Yes' : 'No'}</div>
            </div>
          </div>

          {/* Knockout Results (intake form submissions) */}
          {candidate.source === 'website' && candidate.florida_license_active !== undefined && candidate.florida_license_active !== null && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Intake Qualifications</div>
              <div className="grid grid-cols-2 gap-1">
                {[
                  { label: 'FL License', value: candidate.florida_license_active },
                  { label: 'Own Kit', value: candidate.has_own_kit },
                  { label: '1099 Agreement', value: candidate.agrees_1099 },
                  { label: 'Senior Comfort', value: candidate.comfortable_with_seniors },
                  { label: 'Fluent English', value: candidate.fluent_english },
                  { label: 'Willing to Travel', value: candidate.willing_to_travel },
                ].map(item => (
                  <div key={item.label} className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                    item.value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    <span>{item.value ? '\u2713' : '\u2717'}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
                {candidate.liability_insurance_status && (
                  <div className={`text-xs px-2 py-1 rounded col-span-2 ${
                    candidate.liability_insurance_status === 'not_willing' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    {candidate.liability_insurance_status === 'not_willing' ? '\u2717' : '\u2713'}{' '}
                    Insurance: {candidate.liability_insurance_status === 'has_insurance' ? 'Has it' : candidate.liability_insurance_status === 'will_get_before_start' ? 'Will get' : 'Not willing'}
                  </div>
                )}
                {candidate.experience_years != null && (
                  <div className="text-xs px-2 py-1 rounded col-span-2 bg-blue-50 text-blue-700">
                    Experience: {candidate.experience_years >= 5 ? '5+ years' : candidate.experience_years >= 3 ? '3-5 years' : '1-2 years'}
                  </div>
                )}
              </div>
              {candidate.rejection_reasons && candidate.rejection_reasons.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {candidate.rejection_reasons.map(reason => (
                    <span key={reason} className="inline-block bg-red-100 text-red-800 text-xs px-1.5 py-0.5 rounded">
                      {reason}
                    </span>
                  ))}
                </div>
              )}
              {candidate.license_image_s3_key && (
                <button
                  onClick={async () => {
                    try {
                      const data = await apiService.getCandidateLicenseImage(candidate.id);
                      if (data.download_url) window.open(data.download_url, '_blank');
                    } catch (e) { alert('Failed to load license image: ' + e.message); }
                  }}
                  className="mt-1.5 text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  View License Image
                </button>
              )}
            </div>
          )}

          {/* Skills & Comfort from intake */}
          {candidate.qualifications && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Skills & Preferences</div>
              {candidate.qualifications.comfort && Object.values(candidate.qualifications.comfort).some(Boolean) && (
                <div className="mb-1.5">
                  <span className="text-[10px] text-gray-500 uppercase">Comfort:</span>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {Object.entries(candidate.qualifications.comfort).filter(([,v]) => v).map(([k]) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">{k.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.qualifications.skills && Object.values(candidate.qualifications.skills).some(Boolean) && (
                <div className="mb-1.5">
                  <span className="text-[10px] text-gray-500 uppercase">Skills:</span>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {Object.entries(candidate.qualifications.skills).filter(([,v]) => v).map(([k]) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{k.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.qualifications.availability && Object.values(candidate.qualifications.availability).some(Boolean) && (
                <div>
                  <span className="text-[10px] text-gray-500 uppercase">Available:</span>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {Object.entries(candidate.qualifications.availability).filter(([,v]) => v).map(([k]) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{k.charAt(0).toUpperCase() + k.slice(1,3)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rating */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Rating:</span>
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                onClick={async () => {
                  try {
                    await apiService.updateCandidate(candidate.id, { rating: n });
                    await onRefresh();
                  } catch (e) { console.error(e); }
                }}
                className="focus:outline-none"
              >
                <Star className={`w-4 h-4 ${n <= (candidate.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>

          {/* Cover letter / notes */}
          {candidate.cover_letter && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Cover Letter / Message</div>
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">{candidate.cover_letter}</div>
            </div>
          )}

          {/* Status Transitions */}
          {nextStatuses.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-2">Move to:</div>
              <div className="flex flex-wrap gap-1.5">
                {nextStatuses.map(s => (
                  <Button
                    key={s}
                    size="sm"
                    variant={s === 'hired' ? 'primary' : 'secondary'}
                    onClick={() => onTransition(candidate.id, s)}
                    disabled={saving}
                    className={s === 'rejected' ? 'border-red-200 text-red-700 hover:bg-red-50' : ''}
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    {getStatusLabel(s)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Begin Onboarding */}
          {candidate.status === 'hired' && !candidate.staff && (
            <Button size="sm" onClick={handleBeginOnboarding} className="w-full">
              Begin Onboarding (Create Staff Record)
            </Button>
          )}

          {candidate.staff && (
            <div className="bg-green-50 border border-green-200 rounded p-2 text-xs space-y-1">
              <div className="font-medium text-green-800">Onboarded</div>
              <div className="text-green-700">Staff ID: #{typeof candidate.staff === 'object' ? candidate.staff.id : candidate.staff}</div>
              {candidate.staff_name && <div className="text-green-700">{candidate.staff_name}</div>}
              {typeof candidate.staff === 'object' && candidate.staff.square_id && (
                <div className="text-green-600">Square: linked</div>
              )}
              {typeof candidate.staff === 'object' && candidate.staff.mercury_recipient_id && (
                <div className="text-green-600">Mercury: linked</div>
              )}
              <div className="flex gap-2 pt-1">
                <a href={`/technicians/${typeof candidate.staff === 'object' ? candidate.staff.id : candidate.staff}`} className="text-green-700 underline hover:text-green-900">View Team Member</a>
                <button
                  onClick={async () => {
                    const staffId = typeof candidate.staff === 'object' ? candidate.staff.id : candidate.staff;
                    try {
                      const data = await apiService.getIntakeLink(staffId);
                      await navigator.clipboard.writeText(data.link);
                      alert('Documents link copied!\n\nSend to the team member:\n' + data.link);
                    } catch { alert('Failed to generate link'); }
                  }}
                  className="text-blue-700 underline hover:text-blue-900"
                >
                  Copy Documents Link
                </button>
              </div>
            </div>
          )}

          {/* Notes Timeline */}
          <div>
            <div className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Notes & Activity</span>
            </div>

            {/* Add note */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                placeholder="Add a note..."
                className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
              />
              <Button size="sm" onClick={handleAddNote} disabled={addingNote || !newNote.trim()}>
                {addingNote ? '...' : 'Add'}
              </Button>
            </div>

            {/* Notes list */}
            <div className="space-y-2">
              {(candidate.notes || []).map(note => (
                <div key={note.id} className="border-l-2 border-gray-200 pl-3 py-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-medium">{note.author_name || 'System'}</span>
                    <span>{new Date(note.created_at).toLocaleString()}</span>
                    {note.note_type !== 'note' && (
                      <Badge variant="neutral" small>{note.note_type.replace('_', ' ')}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5">{note.content}</p>
                </div>
              ))}
              {(!candidate.notes || candidate.notes.length === 0) && (
                <p className="text-xs text-gray-400">No notes yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Add Job Posting Modal
// =============================================================================

function AddJobPostingModal({ locations, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', location_id: '', type: 'contract',
    salary_min: '', salary_max: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      setSaving(true);
      const data = { ...form };
      if (!data.location_id) delete data.location_id;
      if (!data.salary_min) delete data.salary_min;
      if (!data.salary_max) delete data.salary_max;
      await apiService.createJobPosting(data);
      onSaved();
    } catch (error) {
      alert('Failed to create job posting: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
      <div className="bg-white border border-gray-200 shadow-lg max-w-lg w-full">
        {/* Header */}
        <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10 border-b border-red-900">
          <div className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            <h2 className="text-sm font-semibold">Create Job Posting</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-3 py-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label>
              <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3} className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <select value={form.location_id} onChange={(e) => setForm({...form, location_id: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  <option value="">Select location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  <option value="contract">Contract</option>
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Salary Min</label>
                <input type="number" value={form.salary_min} onChange={(e) => setForm({...form, salary_min: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Salary Max</label>
                <input type="number" value={form.salary_max} onChange={(e) => setForm({...form, salary_max: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                </select>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-3 py-2 flex justify-end gap-2 border-t border-gray-200 sticky bottom-0">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" type="submit" disabled={saving || !form.title.trim()}>
              {saving ? 'Saving...' : 'Create Posting'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HiringPage;
